import { z } from 'zod';
import { blankMeansAbsent, type BlankMeansAbsent } from '../fields/blank';
import { optionalPhone } from '../fields/phone';
import { optionalText } from '../fields/text';
import type { FieldHint, SelectOption } from '../types';

/**
 * The catalogue of Microsoft Entra user attributes a form can ask for.
 *
 * One entry per Graph `user` property, keyed by **Graph's own property name**, carrying
 * the schema that validates it and the hint that renders it. A form does not write
 * these by hand; it calls {@link pickEntraUser} with the keys it wants and gets a shape
 * and a field list that agree by construction. The registry's "hints exactly match
 * schema keys" invariant is satisfied for free, and a label corrected here is
 * corrected on every form.
 *
 * Why Graph's names: the validated payload lands in the case description and is what a
 * technician (or a script) will eventually feed to `PATCH /users/{id}`. With the keys
 * already Graph's, that step is a copy, not a mapping. The one concession to Graph's
 * shape is `businessPhones`, which is a list there and a single box here — the schema
 * emits a one-element array so the copy still holds.
 *
 * Required-ness is what Graph needs to *create* a user: `userPrincipalName`, `givenName`
 * and `surname`. Everything else is optional and a blank box means "not provided". A
 * form that shows the fields conditionally picks with `optional: true` and requires the
 * creation trio back itself once they are on screen (see `generic-request`).
 */

/** Countries this portal's tenants operate in; ISO 3166-1 alpha-2, as Graph requires. */
export const USAGE_LOCATIONS = ['CA', 'US'] as const;

/** Locales offered for `preferredLanguage`, in Graph's `language-COUNTRY` form. */
export const PREFERRED_LANGUAGES = ['en-CA', 'fr-CA', 'en-US'] as const;

const USAGE_LOCATION_OPTIONS: readonly SelectOption[] = [
  { value: 'CA', label: 'Canada' },
  { value: 'US', label: 'United States' },
];

const PREFERRED_LANGUAGE_OPTIONS: readonly SelectOption[] = [
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'fr-CA', label: 'French (Canada)' },
  { value: 'en-US', label: 'English (United States)' },
];

/** A name as Graph stores it: required, trimmed, within Graph's 64-character limit. */
const requiredName = z.string().trim().min(1, 'Required').max(64, 'Keep this under 64 characters');

/** One hint, minus `name`, which the catalogue key supplies. */
type HintOf<K extends string> = Omit<FieldHint<Record<K, unknown>>, 'name'>;

interface Attribute<K extends string, S extends z.ZodType> {
  schema: S;
  hint: HintOf<K>;
}

function attribute<K extends string, S extends z.ZodType>(
  schema: S,
  hint: HintOf<K>,
): Attribute<K, S> {
  return { schema, hint };
}

export const ENTRA_USER_ATTRIBUTES = {
  /**
   * The sign-in address: an email address in shape, not necessarily a deliverable
   * mailbox.
   */
  userPrincipalName: attribute(z.email('first.last@example.ca'), {
    label: 'Username',
    widget: 'email',
    placeholder: 'first.last@example.ca',
    helpText: 'Create the username for your new team member',
  }),

  givenName: attribute(requiredName, { label: 'First name', widget: 'text' }),

  surname: attribute(requiredName, { label: 'Last name', widget: 'text' }),

  jobTitle: attribute(optionalText(128), { label: 'Job title', widget: 'text' }),

  department: attribute(optionalText(64), { label: 'Department', widget: 'text' }),

  companyName: attribute(optionalText(64), { label: 'Company', widget: 'text' }),

  streetAddress: attribute(optionalText(1024), {
    label: 'Street address',
    widget: 'text',
    helpText: 'The street address of their place of work, as it should appear in the directory.',
  }),

  city: attribute(optionalText(128), { label: 'City', widget: 'text' }),

  state: attribute(optionalText(128), { label: 'Province / state', widget: 'text' }),

  postalCode: attribute(optionalText(40), { label: 'Postal code', widget: 'text' }),

  country: attribute(optionalText(128), {
    label: 'Country',
    widget: 'text',
    defaultValue: 'Canada',
    placeholder: 'Canada',
  }),

  employeeId: attribute(optionalText(16), { label: 'Employee ID', widget: 'text' }),

  mobilePhone: attribute(optionalPhone(), {
    label: 'Mobile phone',
    widget: 'tel',
    placeholder: '(306) 555-0123',
    helpText: 'Optional. Type it any way you like; it is tidied up when you move on.',
  }),

  /**
   * Graph's `businessPhones` is a list. One box here; the schema wraps the number so the
   * stored value is the list Graph expects. Blank stays "not provided", not `[]`.
   */
  businessPhones: attribute(
    optionalPhone().transform((value) => (value === undefined ? undefined : [value])),
    {
      label: 'Business phone',
      widget: 'tel',
      placeholder: '(306) 555-0100',
      helpText: 'Optional. The desk or office number.',
    },
  ),

  /**
   * Required by Graph before a licence can be assigned, and refused unless it is an
   * ISO country code — so a select, prefilled with the country nearly every hire is in.
   */
  usageLocation: attribute(z.enum(USAGE_LOCATIONS, { error: 'Choose a country' }), {
    label: 'Usage location',
    widget: 'select',
    options: USAGE_LOCATION_OPTIONS,
    defaultValue: 'CA',
    helpText: 'The country the person works in. Microsoft 365 licences depend on it.',
  }),

  preferredLanguage: attribute(z.enum(PREFERRED_LANGUAGES, { error: 'Choose a language' }), {
    label: 'Preferred language',
    widget: 'select',
    options: PREFERRED_LANGUAGE_OPTIONS,
    defaultValue: 'en-CA',
  }),
} as const;

export type EntraUserAttribute = keyof typeof ENTRA_USER_ATTRIBUTES;

/** Every attribute, in the order the catalogue declares them. */
export const ENTRA_USER_ALL = Object.keys(ENTRA_USER_ATTRIBUTES) as readonly EntraUserAttribute[];

/** What Graph needs to create a user. The rest is set afterwards. */
export const ENTRA_USER_REQUIRED = ['userPrincipalName', 'givenName', 'surname'] as const;

/**
 * What an update may change: everything but the sign-in name. Changing
 * `userPrincipalName` is a rename with its own consequences (mail, licences, sign-in),
 * and is not something a request form should do in passing.
 */
export const ENTRA_USER_PATCHABLE = ENTRA_USER_ALL.filter(
  (key): key is Exclude<EntraUserAttribute, 'userPrincipalName'> => key !== 'userPrincipalName',
);

type SchemaOf<K extends EntraUserAttribute> = (typeof ENTRA_USER_ATTRIBUTES)[K]['schema'];

export interface PickEntraUserOptions {
  /**
   * `true` wraps every picked schema in `.optional()`, for fields shown conditionally
   * (absent from the payload when hidden). `'blank'` goes further: a box left empty
   * also means "not provided", for an update form where blank means unchanged.
   */
  optional?: boolean | 'blank';
  /** Drop each hint's `defaultValue`, so a select opens on "Choose…" rather than an answer. */
  noDefaults?: boolean;
  /** Applied to every picked hint. */
  visibleWhen?: FieldHint<Record<string, unknown>>['visibleWhen'];
  section?: string;
}

/**
 * What a form gets back. The hint type names the picked keys as *optional* `unknown`
 * on purpose: `FieldHint` is contravariant in its input type (it only uses `keyof`), so
 * a form whose schema makes some of these keys optional must still be able to take
 * these hints, and with `exactOptionalPropertyTypes` a required key would refuse it.
 */
export interface PickedEntraUser<Shape extends z.ZodRawShape> {
  shape: Shape;
  fields: readonly FieldHint<{ [P in keyof Shape]?: unknown }>[];
}

/**
 * The shape and hints for a subset of the catalogue, in the order the keys are given.
 *
 * `optional: true` returns each schema wrapped in `ZodOptional`, which is what a
 * conditionally shown field must be (a hidden field never reaches the payload).
 */
export function pickEntraUser<const K extends EntraUserAttribute>(
  keys: readonly K[],
  options: PickEntraUserOptions & { optional: 'blank' },
): PickedEntraUser<{ [P in K]: BlankMeansAbsent<SchemaOf<P>> }>;
export function pickEntraUser<const K extends EntraUserAttribute>(
  keys: readonly K[],
  options: PickEntraUserOptions & { optional: true },
): PickedEntraUser<{ [P in K]: z.ZodOptional<SchemaOf<P>> }>;
export function pickEntraUser<const K extends EntraUserAttribute>(
  keys: readonly K[],
  options?: PickEntraUserOptions & { optional?: false },
): PickedEntraUser<{ [P in K]: SchemaOf<P> }>;
export function pickEntraUser<const K extends EntraUserAttribute>(
  keys: readonly K[],
  options: PickEntraUserOptions = {},
): PickedEntraUser<z.ZodRawShape> {
  // A plain record while building; ZodRawShape itself is read-only.
  const shape: Record<string, z.ZodType> = {};
  const fields: FieldHint<Partial<Record<string, unknown>>>[] = [];

  for (const key of keys) {
    const { schema, hint } = ENTRA_USER_ATTRIBUTES[key];
    shape[key] =
      options.optional === 'blank'
        ? blankMeansAbsent(schema)
        : options.optional
          ? schema.optional()
          : schema;
    const { defaultValue, ...rest } = hint;
    fields.push({
      name: key,
      ...rest,
      ...(defaultValue !== undefined && !options.noDefaults ? { defaultValue } : {}),
      ...(options.section !== undefined ? { section: options.section } : {}),
      ...(options.visibleWhen !== undefined ? { visibleWhen: options.visibleWhen } : {}),
    });
  }

  return { shape, fields };
}
